<template>
  <PbDialog
    dialog-title="Support session"
    class="pb-support-session-dialog"
    size="medium"
    :state="state"
    transition=""
    @open="open"
    @close="close"
  >
    <template v-if="supportSession && userStore.canManageProfiles">
      <h2>Informations</h2>
      <div class="pb-row pb-half-margin-top">
        <div class="pb-cell-1-2">
          <UiField
            v-tooltip="{ text: supportSession.requestId }"
            nolabel
            nofocus
            label="Request ID"
          >
            <span class="txt-muted txt-right txt-nowrap ellipsis">
              {{ supportSession.requestId }}
            </span>
            <UiSeparator
              before
              after
            />
            <UiBadge
              v-if="supportSession.orderPaid === null"
              color="default"
            >
              Free
            </UiBadge>
            <UiBadge
              v-else-if="supportSession.orderPaid"
              color="success"
            >
              Paid
            </UiBadge>
            <UiBadge
              v-else
              color="error"
            >
              Unpaid
            </UiBadge>
            <UiSeparator before />
            <BaseButton
              tooltip="Open in Regalade"
              :to="supportSession.requestLink"
            >
              <UiIcon icon="show" />
            </BaseButton>
          </UiField>
        </div>
        <div class="pb-cell-1-2 pb-half-margin-left">
          <UiField
            v-tooltip="{ text: supportSession.ticketId }"
            nolabel
            nofocus
            label="Ticket ID"
          >
            <span class="txt-muted txt-right txt-nowrap ellipsis">
              {{ supportSession.ticketId }}
            </span>
            <UiSeparator before />
            <BaseButton
              tooltip="Open in Regalade"
              :to="supportSession.ticketLink"
            >
              <UiIcon icon="show" />
            </BaseButton>
          </UiField>
        </div>
      </div>
      <div class="pb-row pb-half-margin-top">
        <div class="pb-cell-1-2">
          <UiField
            v-tooltip="{ text: supportSession.profileBrand }"
            nolabel
            nofocus
            label="Profile brand"
          >
            <span class="txt-muted txt-right txt-nowrap ellipsis">
              {{ supportSession.profileBrand }}
            </span>
          </UiField>
        </div>
        <div class="pb-cell-1-2 pb-half-margin-left">
          <UiField
            v-tooltip="{ text: supportSession.profileName }"
            nolabel
            nofocus
            label="Profile name"
          >
            <span class="txt-muted txt-right txt-nowrap ellipsis">
              {{ supportSession.profileName }}
            </span>
          </UiField>
        </div>
      </div>
      <div class="pb-row">
        <div class="pb-cell-1-1 pb-half-margin-top">
          <UiField
            v-tooltip="{ text: supportSession.requestAuthorEmail }"
            nolabel
            nofocus
            label="Email of request author"
          >
            <span class="txt-muted txt-right txt-nowrap ellipsis">
              {{ supportSession.requestAuthorEmail }}
            </span>
            <UiSeparator before />
            <BaseButton
              tooltip="Copy email address to clipboard"
              class="hover-info focus-info"
              @click="copyRequestAuthorEmail"
            >
              <UiIcon icon="copy" />
            </BaseButton>
            <UiSeparator />
            <BaseButton
              tooltip="Send an email"
              :to="`mailto:${supportSession.requestAuthorEmail}`"
            >
              <UiIcon icon="message" />
            </BaseButton>
          </UiField>
        </div>
      </div>

      <h2 class="pb-margin-top">
        Actions
      </h2>
      <ButtonGroup
        row
        class="pb-half-padding-top"
      >
        <ControlButton
          :tooltip="supportSession.documentationFilename"
          :to="supportSession.documentationLink"
        >
          <UiIcon icon="paperclip" /><span class="ellipsis">Open documentation</span>
        </ControlButton>

        <ControlButton
          :disabled="openAttachedButton.disabled"
          :tooltip="openAttachedButton.tooltip"
          :to="{
            name: routeNames.Profile,
            params: { profileUid: supportSession.attachedProfileUid },
          }"
        >
          <UiIcon icon="edit-ssl" /><span class="ellipsis">Open attached profile</span>
        </ControlButton>
      </ButtonGroup>

      <ButtonGroup
        row
        class="pb-half-padding-top"
      >
        <ControlButton
          class="btn-info"
          :to="{
            name: routeNames.Create,
            params: {
              source: 'new',
            },
            query: {
              brand: supportSession.profileBrand,
              name: supportSession.profileName,
            },
          }"
          :tooltip="newButtonTooltip"
        >
          <UiIcon icon="add" /><span class="ellipsis">New profile</span>
        </ControlButton>

        <ButtonWithLoader
          v-tooltip="{ text: duplicateButton.tooltip }"
          class="btn-info"
          :disabled="duplicateButton.disabled"
          @click="duplicateOpenedProfile"
        >
          <UiIcon icon="copy" /><span class="ellipsis">Duplicate</span>
        </ButtonWithLoader>
      </ButtonGroup>

      <ButtonGroup
        row
        class="pb-half-padding-top"
      >
        <ButtonWithLoader
          class="btn-success"
          :disabled="officialButton.disabled"
          :tooltip="officialButton.tooltip"
          @click="makeOfficial"
        >
          <UiIcon icon="certified" /><span class="ellipsis">Make official</span>
        </ButtonWithLoader>

        <ButtonWithLoader
          class="btn-warning"
          :disabled="ownershipButton.disabled"
          :tooltip="ownershipButton.tooltip"
          @click="transferOwnership"
        >
          <UiIcon icon="share" /><span class="ellipsis">Transfer ownership</span>
        </ButtonWithLoader>
      </ButtonGroup>
    </template>

    <template
      v-if="supportSession && userStore.canManageProfiles"
      #footer
    >
      <ButtonGroup row>
        <ButtonWithLoader
          class="btn-success"
          :disabled="attachButton.disabled"
          :tooltip="attachButton.tooltip"
          @click="attachOpenedProfile"
        >
          <UiIcon icon="link" /><span class="ellipsis">Attach</span>
        </ButtonWithLoader>

        <ControlButton
          class="btn-error pb-half-margin-top"
          tooltip="Closes this dialog and the banner"
          @click="clearSession"
        >
          <UiIcon icon="exit" /><span class="ellipsis">Leave session</span>
        </ControlButton>
      </ButtonGroup>
    </template>
  </PbDialog>
</template>

<script setup>
import { computed, toRef } from 'vue'
import ControlButton from '@nicolaudie-group/ng-ui-kit/components/ControlButton/ControlButton.vue'
import useAlerter from '@nicolaudie-group/ng-ui-kit/composables/useAlerter'
import useSupportStore from '@/stores/support.store'
import useRouterStore from '@/stores/router.store'
import useUserStore from '@/stores/user.store'
import useProfileStore from '@/stores/profile.store'
import PbDialog from '@/components/PbDialog.vue'
import { useProfile } from '@/composables/useProfile'
import BaseButton from '@nicolaudie-group/ng-ui-kit/components/BaseButton/BaseButton.vue'
import vTooltip from '@nicolaudie-group/ng-ui-kit/directives/vTooltip'
import UiIcon from '@nicolaudie-group/ng-ui-kit/components/UiIcon/UiIcon.vue'
import UiField from '@nicolaudie-group/ng-ui-kit/components/UiField/UiField.vue'
import UiSeparator from '@nicolaudie-group/ng-ui-kit/components/UiField/UiSeparator.vue'
import useClipboard from '@nicolaudie-group/ng-ui-kit/composables/useClipboard'
import UiBadge from '@nicolaudie-group/ng-ui-kit/components/UiBadge/UiBadge.vue'
import routeNames from '@/router/names'
import useTranslate from '@/composables/useTranslate'
import useAction from '@/composables/useAction'
import * as sdk from '@/helpers/sdk'
import ButtonGroup from '../ButtonGroup/ButtonGroup.vue'
import ButtonWithLoader from '../ButtonWithLoader.vue'

defineProps({
  state: { type: Boolean, default: true },
})

const emit = defineEmits(['open', 'close'])

const routerStore = useRouterStore()
const supportStore = useSupportStore()
const userStore = useUserStore()
const profileStore = useProfileStore()
const profile = useProfile()

const { translate } = useTranslate()
const { success } = useAlerter()

const supportSession = toRef(supportStore, 'session')
// const supportSessionRequestId = toRef(supportStore, 'sessuinId')
// const supportSessionStatus = toRef(supportStore, 'sessionStatus')

function close() {
  // When the close button or the overlay of the dialog is clicked or escape is pressed
  emit('close')
}

function open() {
  // When the dialog is opened
  emit('open')
}

function clearSession() {
  close()
  supportStore.leaveSession()
}

async function attachOpenedProfile() {
  if (profile.value) {
    await supportStore.attachProfile(profile.value.uid)
    success({
      title: 'Profile attached',
      text: `"${profile.value.name}" has been attached to request #${supportSession.value.requestId}`,
    })
  }
}

const { action: getUserBrief } = useAction(
  async (userEmail) => {
    const result = await sdk.v2.users.getBriefByEmail({ url: { userEmail } })
    return result?.data
  },
)

async function transferOwnership() {
  const userEmail = supportSession.value.requestAuthorEmail

  if (!(profile.value && userEmail)) return

  const user = await getUserBrief(userEmail)

  if (!user) return

  await profileStore.edit({ ownerUid: user.uid })
  success({
    title: 'Successful transfer',
    text: `The new owner is now ${userEmail}`,
  })
}

async function makeOfficial() {
  if (!profile.value) return

  await profileStore.edit({ isCertificated: true })
}

async function duplicateOpenedProfile() {
  if (profile.value) {
    const { uid } = await profileStore.duplicate()
    await routerStore.push({
      name: routeNames.Create,
      params: {
        profileUid: uid,
        source: 'copy',
      },
      query: {
        brand: supportSession.value.profileBrand,
        name: supportSession.value.profileName,
      },
    })
  }
}

const { copy } = useClipboard()
function copyRequestAuthorEmail() {
  copy(supportSession.value?.requestAuthorEmail)
}

const openAttachedButton = computed(() => {
  if (!supportSession.value.attachedProfileUid) return { disabled: true, tooltip: 'No profile attached to this request' }
  if (profile.value?.uid && profile.value.uid === supportSession.value.attachedProfileUid) return { disabled: true, tooltip: 'Profile is already opened' }
  return { disabled: false, tooltip: 'Open the profile attached to this request' }
})

const newButtonTooltip = 'Create a new profile, and set the name & brand'

const duplicateButton = computed(() => {
  if (!profile.value) return { disabled: true, tooltip: 'Please open a profile first' }
  if (!profile.value.userCanCopy) return { disabled: true, tooltip: translate('pb2.profilePropertiesView.missingProfileCopyPermissionTooltip') }
  return { disabled: false, tooltip: 'Duplicate currently opened profile, and set the name & brand' }
})

const officialButton = computed(() => {
  if (!profile.value) return { disabled: true, tooltip: 'Please open a profile first' }
  if (!profile.value.userCanManage) return { disabled: true, tooltip: 'You do not have permission to manage currently opened profile' }
  if (profile.value.isRemove) return { disabled: true, tooltip: 'Profile was deleted. Restore it first' }
  if (!profile.value.isValid) return { disabled: true, tooltip: 'Currently opened profile contains errors' }
  if (profile.value.isCertificated) return { disabled: true, tooltip: 'Currently opened profile is already official' }
  return { disabled: false, tooltip: 'Make currently opened profile official' }
})

const ownershipButton = computed(() => {
  if (!profile.value) return { disabled: true, tooltip: 'Please open a profile first' }
  if (!profile.value.userCanManage) return { disabled: true, tooltip: 'You do not have permission to manage currently opened profile' }
  if (profile.value.isRemove) return { disabled: true, tooltip: 'Profile was deleted. Restore it first' }
  if (profile.value.isCertificated) return { disabled: true, tooltip: 'Cannot transfer ownership of an official profile' }
  if (profileStore.ownerData?.email === supportSession.value.requestAuthorEmail) return { disabled: true, tooltip: 'User already owns this profile' }
  return { disabled: false, tooltip: `Transfer ownership to ${supportSession.value.requestAuthorEmail}` }
})

const attachButton = computed(() => {
  if (!profile.value) return { disabled: true, tooltip: 'Please open a profile first' }
  if (profile.value.isRemove) return { disabled: true, tooltip: 'Profile was deleted. Restore it first' }
  if (profile.value.uid === supportSession.value.attachedProfileUid) return { disabled: true, tooltip: 'This profile is already attached to this request' }
  return { disabled: false, tooltip: 'Attach currently opened profile to the request' }
})
</script>

<style lang="scss">
@use "sass:math";
@use "@nicolaudie-group/ng-ui-kit/styles/defines/_fonts.define.scss";
@use "@nicolaudie-group/ng-ui-kit/styles/defines/_colors.define.scss";
@use "@nicolaudie-group/ng-ui-kit/styles/defines/_config.define.scss";

@use "@nicolaudie-group/ng-ui-kit/styles/mixins/_flexbox.mixin.scss";
@use "@nicolaudie-group/ng-ui-kit/styles/mixins/_generic.mixin.scss";
@use "@nicolaudie-group/ng-ui-kit/styles/mixins/_responsive.mixin.scss";
@use "@nicolaudie-group/ng-ui-kit/styles/mixins/_theme.mixin.scss";
@use "@nicolaudie-group/ng-ui-kit/styles/mixins/_animations.mixin.scss";
@use "@nicolaudie-group/ng-ui-kit/styles/mixins/_transitions.mixin.scss";

@use '/src/styles/_pb.scss';

</style>
